// src/legal/legalState.ts
//
// The pure rules of the legal acceptance gate. No React and no Firebase import:
// LegalGate pulls this into the entry bundle, everything heavier (the dialog,
// the texts, the PDF builder) stays in the lazily loaded part.

export type LegalKind = 'agree' | 'notice';
export type LegalAction = 'accepted' | 'declined' | 'shown';

export interface LegalVersion {
    version: number;
    notice_days: number;
    /** SHA-256 of the document file, per language. */
    sha256: Record<string, string>;
    /**
     * A version that changes nothing in substance (a typo, a date line): it
     * asks nobody to accept again. Without the flag a version is major.
     */
    minor?: boolean;
    /** Stamped by the publish run; absent in the bundled manifest. */
    published_at?: unknown;
    effective_from?: unknown;
}

export interface LegalDocumentEntry {
    id: string;
    kind: LegalKind;
    title: Record<string, string>;
    versions: LegalVersion[];
}

export interface LegalManifest {
    revision: number;
    documents: LegalDocumentEntry[];
}

export interface LegalAcceptance {
    version: number;
    at: unknown;
}

/**
 * `users/{uid}.legal`. Times are Firestore Timestamps after a normal load and
 * millisecond numbers after `recordLegalDecisions`; read them with `toMillis`.
 */
export interface LegalState {
    accepted?: Record<string, LegalAcceptance>;
    /**
     * From here a new session must deal with the documents first; null when
     * nothing is open. Absent in a record written before this field existed.
     */
    due_at?: unknown;
    /** From here the server refuses: `due_at` plus the session grace. */
    valid_until?: unknown;
}

export interface LegalDecision {
    document_id: string;
    version: number;
    sha256: string;
    language: string;
    action: LegalAction;
}

export interface RecordLegalDecisionsResult {
    ok: true;
    legal: LegalState;
}

export type GateState = 'ok' | 'notice' | 'blocked';

export interface OutstandingDocument {
    document: LegalDocumentEntry;
    newest: LegalVersion;
    /** 0 when the user never accepted this document. */
    acceptedVersion: number;
}

/** Milliseconds since epoch for a Timestamp, a Date or a number; else null. */
export function toMillis(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? ms : null;
    }
    if (!value || typeof value !== 'object') return null;
    const v = value as Record<string, unknown>;
    if (typeof v.toMillis === 'function') {
        return toMillis((v.toMillis as () => unknown).call(value));
    }
    // A Timestamp that lost its prototype (JSON, structured clone).
    const seconds = v.seconds ?? v._seconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        const nanos = v.nanoseconds ?? v._nanoseconds;
        return (
            seconds * 1000 +
            (typeof nanos === 'number' ? Math.floor(nanos / 1e6) : 0)
        );
    }
    return null;
}

/**
 * Whether the record has what the server tests. The server refuses a user
 * without `legal` or without `valid_until`, so such a record blocks here too:
 * a defect can lock someone out but cannot let someone in.
 */
export function isLegalComplete(
    legal: LegalState | null | undefined
): legal is LegalState {
    if (!legal || typeof legal !== 'object') return false;
    return legal.valid_until === null || toMillis(legal.valid_until) !== null;
}

/**
 * What a session starts with, decided from `due_at`: the app (`ok`), the app
 * with the dialog on top (`notice`), or the dialog alone (`blocked`).
 *
 * `due_at` is where a new session has to deal with the documents first. The
 * server only refuses from `valid_until`, a day later, so a session that is
 * already running is never cut off.
 */
export function gateState(
    legal: LegalState | null | undefined,
    nowMs: number
): GateState {
    if (!isLegalComplete(legal)) return 'blocked';
    // A record from before `due_at` existed: there `valid_until` was the
    // deadline itself. The same reading for a `due_at` of null next to a
    // `valid_until` that is set, which the server never writes.
    const due = legal.due_at ?? legal.valid_until;
    if (due === null) return 'ok';
    const at = toMillis(due);
    if (at === null) return 'blocked';
    return at > nowMs ? 'notice' : 'blocked';
}

// config/legal is written by the server, yet whoever lists the documents must
// not fall over an entry that is not what it should be: that would take the
// dialog, or a whole admin page, with it.
function versionsOf(
    document: LegalDocumentEntry | null | undefined
): LegalVersion[] {
    const versions: unknown = document?.versions;
    if (!Array.isArray(versions)) return [];
    return versions.filter(
        (version): version is LegalVersion =>
            Boolean(version) && typeof version.version === 'number'
    );
}

export function newestVersion(
    document: LegalDocumentEntry
): LegalVersion | undefined {
    let newest: LegalVersion | undefined;
    for (const version of versionsOf(document)) {
        if (!newest || version.version > newest.version) newest = version;
    }
    return newest;
}

/**
 * The version of a document a user accepted, 0 for none. Read exactly as the
 * server reads it (`Number(version) || 0` in functions/lib/legal.js), so that
 * a value a defect left there (a numeric string, NaN) gets the same answer on
 * both sides.
 */
export function acceptedVersionOf(
    accepted: LegalState['accepted'] | null | undefined,
    id: string
): number {
    return Number(accepted?.[id]?.version) || 0;
}

/**
 * Whether a user who accepted `acceptedVersion` (0 for none) has to deal with
 * the document:
 * - whoever accepted no version owes it as soon as it has one, whatever the
 *   flags say: there is nothing they could have accepted instead;
 * - whoever accepted one owes it again only for a major version after it. A
 *   minor version never asks anyone again.
 */
export function owesDocument(
    document: LegalDocumentEntry,
    acceptedVersion: number
): boolean {
    const versions = versionsOf(document);
    if (!acceptedVersion) return versions.length > 0;
    return versions.some(
        version => version.version > acceptedVersion && version.minor !== true
    );
}

/**
 * The documents the user still has to deal with, in the order of the list.
 * What is shown and recorded is the newest text, minor or major; accepting it
 * covers every older open version of the document.
 */
export function outstandingDocuments(
    documents: LegalDocumentEntry[] | null | undefined,
    accepted: LegalState['accepted'] | null
): OutstandingDocument[] {
    const open: OutstandingDocument[] = [];
    for (const document of Array.isArray(documents) ? documents : []) {
        if (!document || typeof document.id !== 'string') continue;
        const newest = newestVersion(document);
        if (!newest) continue;
        const acceptedVersion = acceptedVersionOf(accepted, document.id);
        if (owesDocument(document, acceptedVersion)) {
            open.push({ document, newest, acceptedVersion });
        }
    }
    return open;
}
